import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Label,
    Input,
    FormFeedback,
} from "reactstrap"
import { connect } from "react-redux"
import * as Yup from "yup"
import { useFormik } from "formik"

import {
    setBreadcrumbItems,
    getCompanyUsers,
    addNewCompanyUser,
    updateCompanyUser,
    deleteCompanyUser,
} from "../../store/actions"

import { getCompanyRoles } from "../../helpers/fakebackend_helper"

const CompanyUsersList = props => {
    document.title = "Équipe Interne | CRM Entreprise"

    const [modal, setModal] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [currentUser, setCurrentUser] = useState(null)
    const [roles, setRoles] = useState([])

    const breadcrumbItems = [
        { title: "Paramètres", link: "#" },
        { title: "Équipe", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Gestion de l'Équipe", breadcrumbItems)
        props.getCompanyUsers()

        // Fetch company roles
        getCompanyRoles().then(data => setRoles(data))
    }, [])

    const toggle = () => {
        if (modal) {
            setModal(false)
            setCurrentUser(null)
            setIsEdit(false)
        } else {
            setModal(true)
        }
    }

    const handleUserClick = user => {
        setCurrentUser({
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            role_id: user.role_id,
            department: user.department || "",
            job_title: user.job_title || "",
            status: user.status,
        })
        setIsEdit(true)
        toggle()
    }

    const handleDeleteUser = user => {
        if (user.is_owner) {
            alert("Impossible de supprimer le propriétaire de l'entreprise")
            return
        }
        if (window.confirm("Êtes-vous sûr de vouloir retirer cet utilisateur ?")) {
            props.deleteCompanyUser(user)
        }
    }

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            first_name: (currentUser && currentUser.first_name) || "",
            last_name: (currentUser && currentUser.last_name) || "",
            email: (currentUser && currentUser.email) || "",
            password: "",
            role_id: (currentUser && currentUser.role_id) || "",
            department: (currentUser && currentUser.department) || "",
            job_title: (currentUser && currentUser.job_title) || "",
            status: (currentUser && currentUser.status) || "active",
        },
        validationSchema: Yup.object({
            first_name: Yup.string().required("Prénom requis"),
            last_name: Yup.string().required("Nom requis"),
            email: Yup.string().email("Email invalide").required("Email requis"),
            password: isEdit
                ? Yup.string().min(8, "Minimum 8 caractères")
                : Yup.string().min(8, "Minimum 8 caractères").required("Mot de passe requis"),
            role_id: Yup.string().required("Rôle requis"),
        }),
        onSubmit: values => {
            if (isEdit) {
                const updatedUser = {
                    id: currentUser.id,
                    ...values,
                }
                props.updateCompanyUser(updatedUser)
            } else {
                props.addNewCompanyUser(values)
            }
            toggle()
        },
    })

    const companyRoles = roles.filter(r => r.type === 'company')

    return (
        <React.Fragment>
            <div className="container-fluid">
                <Row>
                    <Col className="col-12">
                        <Card>
                            <CardBody>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <CardTitle className="h4 mb-0">Membres de l'Équipe</CardTitle>
                                    <Button color="success" onClick={toggle}>
                                        <i className="mdi mdi-plus me-1"></i> Ajouter un Utilisateur
                                    </Button>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-centered table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Utilisateur</th>
                                                <th>Email</th>
                                                <th>Rôle</th>
                                                <th>Département</th>
                                                <th>Statut</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {props.users && props.users.map((user, key) => (
                                                <tr key={key}>
                                                    <td>
                                                        <div>
                                                            <h5 className="font-size-14 mb-1">
                                                                {user.first_name} {user.last_name}
                                                                {user.is_owner && (
                                                                    <Badge color="primary" className="ms-2">
                                                                        Propriétaire
                                                                    </Badge>
                                                                )}
                                                            </h5>
                                                            <p className="text-muted mb-0">{user.job_title}</p>
                                                        </div>
                                                    </td>
                                                    <td>{user.email}</td>
                                                    <td>
                                                        <Badge color="info">{user.role_name}</Badge>
                                                    </td>
                                                    <td>{user.department || "-"}</td>
                                                    <td>
                                                        <Badge color={user.status === 'active' ? 'success' : 'secondary'}>
                                                            {user.status === 'active' ? 'Actif' : 'Inactif'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                onClick={() => handleUserClick(user)}
                                                            >
                                                                <i className="mdi mdi-pencil"></i>
                                                            </Button>
                                                            {!user.is_owner && (
                                                                <Button
                                                                    color="danger"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteUser(user)}
                                                                >
                                                                    <i className="mdi mdi-delete"></i>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            <Modal isOpen={modal} toggle={toggle} size="lg">
                <ModalHeader toggle={toggle}>
                    {isEdit ? "Modifier l'Utilisateur" : "Ajouter un Utilisateur"}
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={validation.handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Prénom</Label>
                                    <Input
                                        name="first_name"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.first_name || ""}
                                        invalid={validation.touched.first_name && validation.errors.first_name}
                                    />
                                    {validation.touched.first_name && validation.errors.first_name && (
                                        <FormFeedback type="invalid">{validation.errors.first_name}</FormFeedback>
                                    )}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Nom</Label>
                                    <Input
                                        name="last_name"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.last_name || ""}
                                        invalid={validation.touched.last_name && validation.errors.last_name}
                                    />
                                    {validation.touched.last_name && validation.errors.last_name && (
                                        <FormFeedback type="invalid">{validation.errors.last_name}</FormFeedback>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <Label>Email</Label>
                            <Input
                                name="email"
                                type="email"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.email || ""}
                                invalid={validation.touched.email && validation.errors.email}
                            />
                            {validation.touched.email && validation.errors.email && (
                                <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                            )}
                        </div>

                        <div className="mb-3">
                            <Label>Mot de passe {isEdit && "(laisser vide pour ne pas modifier)"}</Label>
                            <Input
                                name="password"
                                type="password"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.password || ""}
                                invalid={validation.touched.password && validation.errors.password}
                            />
                            {validation.touched.password && validation.errors.password && (
                                <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                            )}
                        </div>

                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Rôle</Label>
                                    <Input
                                        name="role_id"
                                        type="select"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.role_id || ""}
                                        invalid={validation.touched.role_id && validation.errors.role_id}
                                    >
                                        <option value="">Sélectionner un rôle</option>
                                        {companyRoles.map(role => (
                                            <option key={role.id} value={role.id}>{role.name}</option>
                                        ))}
                                    </Input>
                                    {validation.touched.role_id && validation.errors.role_id && (
                                        <FormFeedback type="invalid">{validation.errors.role_id}</FormFeedback>
                                    )}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Statut</Label>
                                    <Input
                                        name="status"
                                        type="select"
                                        onChange={validation.handleChange}
                                        value={validation.values.status || ""}
                                    >
                                        <option value="active">Actif</option>
                                        <option value="inactive">Inactif</option>
                                    </Input>
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Département</Label>
                                    <Input
                                        name="department"
                                        type="text"
                                        onChange={validation.handleChange}
                                        value={validation.values.department || ""}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Poste</Label>
                                    <Input
                                        name="job_title"
                                        type="text"
                                        onChange={validation.handleChange}
                                        value={validation.values.job_title || ""}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <div className="text-end mt-4">
                            <Button type="button" color="secondary" className="me-2" onClick={toggle}>
                                Annuler
                            </Button>
                            <Button type="submit" color="success">
                                {isEdit ? "Mettre à jour" : "Créer"}
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    users: state.CompanyUsers.users,
})

export default connect(mapStateToProps, {
    setBreadcrumbItems,
    getCompanyUsers,
    addNewCompanyUser,
    updateCompanyUser,
    deleteCompanyUser,
})(CompanyUsersList)
